import { constructRefreshTokenRenewPayload } from "./PayloadUtils";
import { getRefreshTokenRenewUrl } from "./UrlUtilities";
import { cookieOptions } from "./BasicUtilities";
import Cookies from 'js-cookie';

export function isJwtTokenAboutToExpireOrExpired(jwtToken) {
    if (!jwtToken){ // treat missing token as expired
        return true;
    }

    try {
        const payload = JSON.parse(atob(jwtToken.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        const timeRemaining = payload.exp - currentTime;

        // If 1 hour or less remains, consider it about to expire (proactively renew).
        return timeRemaining <= 3600;
    } catch (error) {
        console.error("Invalid JWT token:", error);
        return true; // Treat invalid token as expired
    }
}

// Strict expiry check (exp already passed), distinct from the 5-minute "about to expire"
// heuristic above. Used to decide whether a still-valid token can carry the current request
// when a renewal transiently fails — so a network blip near the renew window doesn't log an
// active user out.
export function isJwtTokenExpired(jwtToken) {
    if (!jwtToken) return true;
    try {
        const payload = JSON.parse(atob(jwtToken.split('.')[1]));
        return payload.exp - Math.floor(Date.now() / 1000) <= 0;
    } catch (error) {
        return true;
    }
}


export function refreshTokenTimeToExpire(refreshToken) {
    if (!refreshToken){ // treat missing token as expired
        return 0;
    }

    try {
        const payload = JSON.parse(atob(refreshToken.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        const timeRemaining = payload.exp - currentTime;

        // If 5 minutes or less remain, consider it about to expire
        return timeRemaining;
    } catch (error) {
        console.error("Invalid JWT token:", error);
        return 0; // Treat invalid token as expired
    }
}

// Single-flight guard for token renewal. Every authenticated request calls
// validateAndRenewIfNeeded first; when the JWT is near expiry they'd each independently
// rotate the (one-per-user) refresh token, and all but the winner would send an
// already-rotated token → "refresh revoked" → forced logout. Bursts of concurrent calls
// (e.g. a form that fetches several endpoints at once) hit this reliably. Coalescing all
// concurrent renewals onto one shared promise makes the rotation happen exactly once.
let renewInFlight = null;

// Renew in a SINGLE round trip: /renew-refresh-token rotates the refresh token AND returns a
// fresh JWT alongside it (TokenPairBare), so there's no need for a second /renew-jwt-token call.
// One call = half the renew-throttle usage and one fewer failure point / rotation-race window.
// Returns { jwt, transient }: `jwt` is the new access token (or undefined on failure); `transient`
// is true when the failure looks recoverable (network / throttle 429 / 5xx) rather than a genuine
// session end (401 = refresh revoked/expired, or the refresh token is already gone).
async function _doRenew(CallApi) {
    const refreshToken = Cookies.get('refresh_token');
    if (!refreshToken || refreshTokenTimeToExpire(refreshToken) <= 0) {
        return { jwt: undefined, transient: false }; // refresh genuinely missing/expired
    }

    const resp = await CallApi({
        url: getRefreshTokenRenewUrl(),
        method: 'POST',
        payload: constructRefreshTokenRenewPayload(refreshToken),
        token: false,
        silent: true, // failure is handled here; the caller shows the graceful notice
    });

    const newRefresh = resp?.data?.refresh_token;
    const newJwt = resp?.data?.jwt_token;
    if (resp?.success && newRefresh && newJwt) {
        Cookies.set('refresh_token', newRefresh, cookieOptions());
        Cookies.set('jwt_token', newJwt, cookieOptions());
        return { jwt: newJwt, transient: false };
    }
    // 401 = the refresh token was revoked/expired server-side → genuine session end.
    // Anything else (429 throttle, 5xx, network "Failed to fetch" → status 500/0) is transient.
    return { jwt: undefined, transient: resp?.status !== 401 };
}

export async function validateAndRenewIfNeeded(jwt_token, CallApi) {
    if (!isJwtTokenAboutToExpireOrExpired(jwt_token)) {
        return jwt_token;
    }
    // Coalesce concurrent renewals: if one is already running, await its result rather than
    // starting a second rotation with the same (about-to-be-stale) refresh token.
    if (!renewInFlight) {
        renewInFlight = _doRenew(CallApi).finally(() => { renewInFlight = null; });
    }
    const { jwt, transient } = await renewInFlight;
    if (jwt) return jwt;

    // Renewal didn't return a new token. Before ending the session, avoid logging out an
    // ACTIVE user over a recoverable hiccup:
    //   1. Another browser tab may have just rotated the tokens — the jwt cookie now holds a
    //      fresh, valid token different from the one we set out to renew. Use it.
    const cookieJwt = Cookies.get('jwt_token');
    if (cookieJwt && cookieJwt !== jwt_token && !isJwtTokenExpired(cookieJwt)) {
        return cookieJwt;
    }
    //   2. The failure was transient (network/throttle/5xx) and our current token hasn't actually
    //      expired yet (it was only within the 5-minute renew window). Carry this request on the
    //      still-valid token; renewal is retried on the next call.
    if (transient && jwt_token && !isJwtTokenExpired(jwt_token)) {
        return jwt_token;
    }
    // Genuine session end (refresh revoked/expired, or the access token is truly expired).
    return undefined;
}

export function getUserData(jwtToken) {
    const userData = JSON.parse(atob(jwtToken.split('.')[1]));
    return userData;
}


